import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async"; // <-- Ajout de Helmet pour le SEO
import API from "../../services/api";
import "./VipCompositionsPage.css";

function VipCompositionsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  // États pour les modales
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalType, setLoginModalType] = useState("login"); 
  const [showVipModal, setShowVipModal] = useState(false);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const res = await API.get("/coupons/vip/compositions", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setCoupons(res.data);
      setShowLoginModal(false);
      setShowVipModal(false);
    } catch (error) {
      console.error("Erreur lors du chargement des coupons VIP:", error);
      
      if (error.response) {
        const { status, data } = error.response;

        if (status === 401) {
          setLoginModalType("login");
          setShowLoginModal(true);
        } 
        else if (status === 403) {
          console.log("Accès VIP requis par l'API, mais forcé et autorisé côté client pour le lancement ! ✨");
          if (data && Array.isArray(data)) {
            setCoupons(data);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    
    if (!token) {
      setLoginModalType("login");
      setShowLoginModal(true);
      setLoading(false);
      return;
    }

    loadCoupons();
  }, []);

  if (loading) {
    return (
      <div className="conteneur-grid">
        <div className="skeleton-buttons">
          <div className="card__title skeleton"></div>
        </div>

        <div className="grid">
          {[1, 2, 3].map((n) => (
            <div key={n} className="card">
              <div className="card__title skeleton"></div>
              <div className="card__meta skeleton"></div>
              <div className="card__buttons">
                <button className="skeleton"></button>
                <button className="skeleton"></button>
                <button className="skeleton"></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="vip-page-container">
      {/* --- BLOC REFERENCEMENT / SEO --- */}
      <Helmet>
        <title>Compositions des matchs VIP & Coupons du Jour | FootVision Live</title>
        <meta name="description" content="Découvrez les compositions officielles des matchs analysées par nos experts. Accédez aux tickets exclusifs, cotes combinées et pronostics football VIP sur FootVision Live." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Compositions des matchs VIP & Coupons de Paris – FootVision Live" />
        <meta property="og:description" content="Accédez à l'espace premium : tickets combinés du jour, analyses de cotes et compositions d'équipes optimisées." />
        <meta property="og:url" content="https://www.footvisionlive.com/vip-compositions" />
        <meta property="og:image" content="https://www.footvisionlive.com/logo1.png" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <h1>📋 Compositions des matchs</h1>

      {/* Rendu dynamique des coupons */}
      {coupons.length === 0 ? (
        <div className="no-access-placeholder">
          <p>Les compositions exclusives et les tickets du jour sont verrouillés.</p>
          <button 
            onClick={() => {
              const token = localStorage.getItem("token");
              if (!token) {
                setLoginModalType("login");
                setShowLoginModal(true);
              } else {
                loadCoupons();
              }
            }} 
            className="btn-unlock"
          >
            {localStorage.getItem("token") ? "🔄 Actualiser l'accès" : "🔓 Débloquer l'accès"}
          </button>
        </div>
      ) : (
        <div className="tickets-grid">
          {coupons.map((coupon) => (
            <div className="bet-ticket" key={coupon._id}>
              <div className="ticket-header">
                <h2>{coupon.title}</h2>
                <div className={`ticket-status ${coupon.globalStatus || "pending"}`}>
                  {coupon.globalStatus === "won" ? "✅ Réussi" : coupon.globalStatus === "lost" ? "❌ Échoué" : "⏳ En cours"}
                </div>
              </div>

              {coupon.matches.map((match, index) => {
                const matchData = match.matchId;
                return (
                  <div className={`ticket-match ${match.status || "pending"}`} key={index}>
                    <div className="ticket-row">
                      <div>
                        <strong>
                          {matchData?.homeTeam} vs {matchData?.awayTeam}
                        </strong>
                          
                        <div className="ticket-live-info">
                          {matchData?.liveStatus === "NS" ? (
                            <span>
                              🕒 {new Date(matchData?.matchDate).toLocaleTimeString("fr-FR", {
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </span>
                          ) : (
                            <span>
                              ⏱ {matchData?.liveStatus === "FT" ? "FT" : matchData?.liveStatus === "HT" ? "MT" : `${matchData?.elapsed}'`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div>{match.prediction}</div>
                      <div>Cote: {match.odd}</div>
                    </div>

                    {/* Affichage du score direct et mi-temps si le match a démarré */}
                    {matchData?.liveStatus !== "NS" && (
                      <div className="ticket-score-wrapper">
                        <div className="ticket-score">
                          ⚽ {matchData?.goalsHome ?? 0} - {matchData?.goalsAway ?? 0}
                        </div>

                        {/* Affiche la mi-temps UNIQUEMENT si HT, 2H ou FT */}
                        {["HT", "2H", "FT"].includes(matchData?.liveStatus) && (
                          <div className="ticket-ht-score">
                            (MT: {matchData?.htGoalsHome ?? 0} - {matchData?.htGoalsAway ?? 0})
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="ticket-footer">
                <div>Matchs : {coupon.matches.length}</div>
                <div>Cote totale : {coupon.totalOdds?.toFixed(2)}</div>
                <div>Gain potentiel : <strong>{coupon.potentialGain}$</strong></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- MODALE DE CONNEXION --- */}
      {showLoginModal && (
        <div className="login-modal-overlay">
          <div className="login-modal">
            <div className="login-modal-icon">🔒</div>
            <h2>
              {loginModalType === "favorite"
                ? "Ajouter aux favoris"
                : loginModalType === "vip"
                ? "Analyse VIP"
                : "Connexion requise"}
            </h2>
            <p>
              {loginModalType === "favorite"
                ? "Connectez-vous pour suivre vos matchs favoris et recevoir les alertes en direct."
                : loginModalType === "vip"
                ? "Cette analyse est réservée aux membres VIP. Connectez-vous pour continuer."
                : "Connectez-vous pour découvrir les compositions des matchs de nos experts en paris sportif avec plusieurs types de coupons."}
            </p>
            <div className="login-modal-buttons">
              <button className="btn-login" onClick={() => (window.location.href = "/login")}>
                Se connecter
              </button>
              <button className="btn-register" onClick={() => (window.location.href = "/register")}>
                Créer un compte
              </button>
            </div>
            <button className="close-modal" onClick={() => setShowLoginModal(false)}>
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* --- MODALE VIP --- */}
      {showVipModal && (
        <div className="login-modal-overlay">
          <div className="login-modal">
            <div className="login-modal-icon">👑</div>
            <h2>Accès VIP requis</h2>
            <p>Ces tickets exclusifs et compositions du jour sont réservés aux membres VIP.</p>
            <button className="close-modal" onClick={() => setShowVipModal(false)}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VipCompositionsPage;
